package presentation.screens.search

import analytics.Analytics
import androidx.lifecycle.viewModelScope
import com.kinoshazam.Navigator
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.data.GlobalStateRepository
import com.kinoshazam.model.Route
import crashreporting.CrashReporter
import data.VideoRepository
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import org.koin.core.component.KoinComponent
import utils.BaseViewModel


class SearchViewModel(
    private val repository: VideoRepository,
    private val navigator: Navigator,
    private val analytics: Analytics,
    private val crashReporter: CrashReporter,
    private val globalStateRepository: GlobalStateRepository,
) : BaseViewModel<SearchState, SearchMVIAction>(), KoinComponent {

    override fun initState(): SearchState = SearchState(
        identifyEnabled = globalStateRepository.features.value.identify,
    )

    private val coroutineExceptionHandler = CoroutineExceptionHandler { _, error ->
        crashReporter.capture(error)
        updateState {
            copy(
                isLoading = false,
                isError = true
            )
        }
    }
    private var currentPage: Int = 1

    private var job: Job? = null

    init {
        viewModelScope.launch {
            globalStateRepository.features.collect { features ->
                updateState {
                    copy(
                        identifyEnabled = features.identify,
                        identifyMode = identifyMode && features.identify,
                    )
                }
            }
        }
    }

    override fun onAction(action: SearchMVIAction) {
        when (action) {
            is SearchMVIAction.OnTextChanged -> onLiveTitleChanged(action.query)
            is SearchMVIAction.OnIdentifyTextChanged -> onLiveIdentifyChanged(action.query)
            is SearchMVIAction.OnQueryChanged -> updateQuery(action.query)
            is SearchMVIAction.OnIdentifyQueryChanged -> updateIdentifyQuery(action.query)
            is SearchMVIAction.SubmitSearch -> submitSearch()
            is SearchMVIAction.ToggleIdentifyMode -> toggleIdentifyMode()
            is SearchMVIAction.FetchNextPage -> fetchNextPage()
            is SearchMVIAction.Reload -> submitSearch()
            is SearchMVIAction.OnVideoClicked -> navigator.navigateTo(
                Route.Dialog.Details(
                    action.id,
                    action.isTcShow
                )
            )

            is SearchMVIAction.OnPersonClicked -> navigator.navigateTo(
                Route.Dialog.Person(action.personId)
            )

            is SearchMVIAction.BackClicked -> navigator.back()
        }
    }

    private fun updateQuery(query: String) {
        updateState {
            copy(
                query = query,
                videos = if (!identifyMode && query.isEmpty()) null else videos,
                persons = if (!identifyMode && query.isEmpty()) null else persons,
                isError = if (!identifyMode && query.isEmpty()) false else isError,
            )
        }
    }

    private fun updateIdentifyQuery(query: String) {
        updateState {
            copy(
                identifyQuery = query,
                videos = if (identifyMode && query.isEmpty()) null else videos,
                persons = if (identifyMode && query.isEmpty()) null else persons,
                isError = if (identifyMode && query.isEmpty()) false else isError,
            )
        }
    }

    private fun toggleIdentifyMode() {
        if (!state.value.identifyEnabled) return
        val enabling = !state.value.identifyMode
        job?.cancel()
        updateState {
            copy(
                identifyMode = enabling,
                videos = null,
                persons = null,
                isError = false,
                isLoading = false,
                isNextLoading = false,
            )
        }
        val query = if (enabling) {
            state.value.identifyQuery.trim()
        } else {
            state.value.query.trim()
        }
        if (query.isNotEmpty()) {
            submitSearch()
        }
    }

    private fun submitSearch() {
        val current = state.value
        val query = if (current.identifyMode) {
            current.identifyQuery.trim()
        } else {
            current.query.trim()
        }
        if (query.isEmpty()) return
        job?.cancel()
        job = viewModelScope.launch(coroutineExceptionHandler) {
            if (state.value.identifyMode) {
                loadIdentifyResults(query)
            } else {
                loadSearchResults(query)
            }
        }
    }

    private fun onLiveTitleChanged(query: String) {
        if (state.value.query == query) return
        val identifyMode = state.value.identifyMode
        job?.cancel()
        updateState {
            copy(
                query = query,
                videos = if (!identifyMode && query.isEmpty()) null else videos,
                persons = if (!identifyMode && query.isEmpty()) null else persons,
                isError = if (!identifyMode && query.isEmpty()) false else isError,
            )
        }
        if (identifyMode || query.isEmpty()) return
        job = viewModelScope.launch(coroutineExceptionHandler) {
            delay(SearchDebounceMs)
            loadSearchResults(query)
        }
    }

    private fun onLiveIdentifyChanged(query: String) {
        if (state.value.identifyQuery == query) return
        val identifyMode = state.value.identifyMode
        job?.cancel()
        updateState {
            copy(
                identifyQuery = query,
                videos = if (identifyMode && query.isEmpty()) null else videos,
                persons = if (identifyMode && query.isEmpty()) null else persons,
                isError = if (identifyMode && query.isEmpty()) false else isError,
            )
        }
        if (!identifyMode || query.isEmpty()) return
        job = viewModelScope.launch(coroutineExceptionHandler) {
            delay(IdentifyDebounceMs)
            loadIdentifyResults(query)
        }
    }

    private suspend fun loadSearchResults(query: String) {
        currentPage = 1
        updateState {
            copy(
                query = query,
                isLoading = true,
                isError = false,
            )
        }
        val videoList = repository.searchData(query, currentPage)
        val videos = videoList?.result.orEmpty()
        val persons = videoList?.persons.orEmpty()
        analytics.track(
            AppEvents.searchSubmitted(
                queryLength = query.length,
                resultCount = videos.size + persons.size,
            )
        )
        updateState {
            copy(
                isLoading = false,
                isError = false,
                videos = videos,
                persons = persons,
            )
        }
    }

    private suspend fun loadIdentifyResults(query: String) {
        updateState {
            copy(
                identifyQuery = query,
                isLoading = true,
                isError = false,
                isNextLoading = false,
            )
        }
        val videoList = repository.identifyText(query)
        analytics.track(
            AppEvents.identifySubmitted(
                queryLength = query.length,
                resultCount = videoList.size,
            )
        )
        updateState {
            copy(
                isLoading = false,
                isError = false,
                videos = videoList,
                persons = emptyList(),
            )
        }
    }

    private fun fetchNextPage() {
        val current = state.value
        if (current.identifyMode) return
        if (current.query.isBlank() || current.isNextLoading || current.isLoading) return
        viewModelScope.launch(coroutineExceptionHandler) {
            updateState {
                copy(isNextLoading = true, isError = false)
            }
            val fetchVideoList = repository.searchData(state.value.query, ++currentPage)

            fetchVideoList?.let { page ->
                updateState {
                    copy(
                        isNextLoading = false,
                        isError = false,
                        videos = videos.orEmpty() + page.result.orEmpty(),
                        persons = (persons.orEmpty() + page.persons.orEmpty())
                            .distinctBy { it.id },
                    )
                }
            } ?: run {
                updateState {
                    copy(isNextLoading = false, isError = false)
                }
            }
        }
    }

    private companion object {
        const val SearchDebounceMs = 1000L
        const val IdentifyDebounceMs = 1500L
    }
}
